import _uniqueId from "lodash/uniqueId";
import {
  FIELD_SUBTEMPLATE_KEY,
  FORM_ROLES_KEY,
  FORM_SUBTEMPLATES_KEY
} from "PFApp/constants/activity_form/activity_form_field_keys";
import { useMultiActivityFormStoreActions } from "PFApp/stores/activity_form/multi_roles_form";
import { MultiActivityForm } from "PFApp/types/activity_form/activity_form_schema";
import { useFieldArray, useFormContext } from "react-hook-form";

import { useInitialRoleData } from "./use_initial_role_data";

type UseSubtemplatesFieldArray = {
  fieldsBlacklist?: string[];
};

export const useSubtemplatesFieldArray = ({ fieldsBlacklist }: UseSubtemplatesFieldArray = {}) => {
  const { control, setValue, getValues } = useFormContext<MultiActivityForm>();
  const { getInitialRoleData } = useInitialRoleData({ fieldsBlacklist });
  const { setRoleData, clearRoleData } = useMultiActivityFormStoreActions();

  const {
    fields: subtemplatesFields,
    append,
    remove
  } = useFieldArray<MultiActivityForm, typeof FORM_SUBTEMPLATES_KEY>({
    name: FORM_SUBTEMPLATES_KEY,
    control
  });

  const addRoleInSubtemplate = (subtemplateKey: string) => {
    const uniqueId = _uniqueId();
    const subtemplateIndex = subtemplatesFields.findIndex(
      (field) => field[FIELD_SUBTEMPLATE_KEY] === subtemplateKey
    );
    const newEmptyRole = getInitialRoleData({ subtemplateKey, isNew: true, uniqueId });
    if (subtemplateIndex >= 0) {
      const rolesKey = `${FORM_SUBTEMPLATES_KEY}.${subtemplateIndex}.${FORM_ROLES_KEY}` as const;
      const rolesValue = getValues(rolesKey);
      setValue(rolesKey, [...rolesValue, newEmptyRole], { shouldDirty: true });
      setRoleData(uniqueId);
      return;
    }

    append({
      [FIELD_SUBTEMPLATE_KEY]: subtemplateKey,
      [FORM_ROLES_KEY]: [newEmptyRole]
    });
    setRoleData(uniqueId);
  };

  const removeSubtemplate = (index: number) => {
    const roles = getValues(`${FORM_SUBTEMPLATES_KEY}.${index}.${FORM_ROLES_KEY}`);
    roles.forEach(({ uniqueId }: any) => {
      clearRoleData(uniqueId);
    });
    remove(index);
  };

  return {
    subtemplatesFields,
    addRoleInSubtemplate,
    removeSubtemplate
  };
};
